import axios from '@/plugins/api-v2-axios'
import toast from 'izitoast'

import {
    successResult,
    failureResult,
    bearerToken,
    responseHandler,
    getOrganisationHeader,
    getOrganisationSelectionHeader,
    getTimeZoneHeader,
    getLocaleHeader,
    getToastError
} from './helpers'

const handleApiSuccess = () => (response) => {
    const { data, status } = response

    return {
        isSuccess: true,
        payload: data,
        status
    }
}

const handleApiFailure = config => (content) => {
    if (!config.suppressError) {
        toast.error(getToastError(content))
    }

    const payload = null != content && null != content.response ? content.response.data : content
    const status = null != content && null != content.response ? content.response.status : undefined
    return {
        isSuccess: false,
        payload,
        status
    }
}

/**
 * Base API class.
 */
export default class BaseApi {
    defaultContentType;
    successResult;
    failureResult;
    bearerToken;

    defaultAnonymousApiConfiguration = {
        useBearerToken: false,
        includeTimeZone: false,
        includeLocale: false,
        contentType: 'application/json',
        suppressError: false
    };

    constructor () {
        this.defaultContentType = 'application/json'
        this.successResult = successResult
        this.failureResult = failureResult
        this.bearerToken = bearerToken
    }

    /**
     * Retrieve the content type header, or default to this.defaultContentType.
     */
    getContentTypeHeader (contentType) {
        return {
            'Content-Type': contentType || this.defaultContentType
        }
    }

    /**
 * Retrieve authorization header including bearer token.
 */
    getAuthorizationHeader () {
        const token = this.bearerToken()
        return {
            Authorization: token ? `Bearer ${token}` : null,
            withCredentials: false
        }
    }

    /**
 * Retrieve all headers based on parameters.
 */
    getHeaders (useBearerToken, contentType = 'application/json') {
        return {
            ...(useBearerToken ? this.getAuthorizationHeader() : null),
            ...(this.getContentTypeHeader(contentType)),
            ...(getOrganisationHeader()),
            ...(getOrganisationSelectionHeader())
        }
    }

    resolveBearerToken () {
        const token = this.bearerToken()
        return {
            Authorization: token ? `Bearer ${token}` : null
        }
    }

    resolveHeaders (configuration) {
        return {
            ...(configuration.useBearerToken ? this.resolveBearerToken() : null),
            ...this.getContentTypeHeader(configuration.contentType),
            ...getOrganisationHeader(),
            ...getOrganisationSelectionHeader(),
            ...(configuration.includeTimeZone ? getTimeZoneHeader() : null),
            ...(configuration.includeLocale ? getLocaleHeader() : null)
        }
    }

    resolveAxiosOptions (configuration, params, timeout = 20000) {
        return {
            baseURL: process.env.VUE_APP_API_HOST_V2,
            headers: this.resolveHeaders(configuration),
            params,
            timeout
        }
    }

    async makeAjaxV2 (
        properties
    ) {
        const { url, method, params, config, data, timeout } = properties
        const useConfig = {
            suppressError: false,
            useBearerToken: true,
            includeTimeZone: false,
            includeLocale: false,
            contentType: 'application/json',
            ...config
        }

        const axiosOptions = this.resolveAxiosOptions(useConfig, params, timeout)

        const cleanUrl = '/' === url[0] ? url : `/${url}`
        const encodedUrl = encodeURI(`${cleanUrl}`)

        if ('get' === method || 'delete' === method) {
            return axios[method](encodedUrl, axiosOptions).then(
                handleApiSuccess(),
                handleApiFailure(useConfig)
            )
        }

        return axios[method](encodedUrl, data, axiosOptions).then(
            handleApiSuccess(),
            handleApiFailure(useConfig)
        )
    }

    async getV2 (
        getProperties
    ) {
        const { url, params, config, timeout } = getProperties
        return this.makeAjaxV2({
            method: 'get',
            url,
            params,
            config,
            timeout
        })
    }

    async postV2 (
        postProperties
    ) {
        const { url, data, params, config, timeout } = postProperties
        return this.makeAjaxV2({
            method: 'post',
            url,
            data,
            params,
            config,
            timeout
        })
    }

    async patch (patchProperties) {
        const { url, data, params, config, timeout } = patchProperties
        return this.makeAjaxV2({
            method: 'patch',
            url,
            data,
            params,
            config,
            timeout
        })
    }

    async putV2 (
        putProperties
    ) {
        const { url, data, params, config, timeout } = putProperties
        return this.makeAjaxV2({
            method: 'put',
            url,
            data,
            params,
            config,
            timeout
        })
    }

    async deleteV2 (
        deleteProperties
    ) {
        const { url, params, config, timeout } = deleteProperties
        return this.makeAjaxV2({
            method: 'delete',
            url,
            params,
            config,
            timeout
        })
    }

    /**
 * Wrapper around axios to call XHR.
 */
    async makeAjax (
        method,
        url,
        data,
        useBearerToken = true,
        params = null,
        contentType = 'application/json',
        apiOptions = { suppressError: false },
        timeout = 120000
    ) {
        const options = {
            headers: this.getHeaders(useBearerToken, contentType),
            params,
            timeout
        }

        const cleanUrl = '/' === url[0] ? url : `/${url}`
        const encodedUrl = encodeURI(`${cleanUrl}`)

        // For GET and DELETE requests
        if ('get' === method || 'delete' === method) {
            return axios[method](encodedUrl, options).then(
                responseHandler(true, apiOptions),
                responseHandler(false, apiOptions)
            )
        }

        // For PUT and POST requests
        return axios[method](encodedUrl, data, options).then(
            responseHandler(true),
            responseHandler(false)
        )
    }

    /**
 * Perform an HTTP GET request.
 */
    async get (
        url,
        params = null,
        useBearerToken = true,
        apiOptions
    ) {
        return this.makeAjax('get', url, null, useBearerToken, params, undefined, apiOptions)
    }

    /**
 * Perform an HTTP POST request.
 */
    async post (
        url,
        body = null,
        params = null,
        useBearerToken = true,
        apiOptions
    ) {
        return this.makeAjax('post', url, body, useBearerToken, params, undefined, apiOptions)
    }

    /**
 * Perform an HTTP PUT request.
 */
    async put (
        url,
        body = null,
        params = null,
        useBearerToken = true,
        apiOptions
    ) {
        return this.makeAjax('put', url, body, useBearerToken, params, undefined, apiOptions)
    }

    /**
 * Perform an HTTP DELETE request.
 */
    async delete (
        url,
        params = null,
        useBearerToken = true,
        apiOptions
    ) {
        return this.makeAjax('delete', url, null, useBearerToken, params, undefined, apiOptions)
    }
}
